import * as React from 'react';
import {client} from '@logilab/cwclientlibjs';
import {RqlParser} from './RqlParser';

export interface RqlQuerierProps {
    rqlClient: client.RqlClient;
    readonly?: boolean;
}

function getQueryVariables(query: string): string[] | null {
    let variables = null;
    let queryMatch = query.match(
        /Any\s([A-Z_,\s()]+?)\s(?=WHERE\s|ORDERBY\s|OFFSET\s|LIMIT\s|GROUPBY\s)/
    );
    if (queryMatch !== null) {
        variables = queryMatch[1].split(',');
        variables.map((varname) => varname.trim());
    }
    return variables;
}

export type RqlQueryResult =
    | {
          rows: client.RqlRows;
          status: 'success';
      }
    | {reason: string; status: 'error'};

function rqlQueryResultSucces(rows: client.RqlRows): RqlQueryResult {
    return {rows, status: 'success'};
}
function rqlQueryResultError(reason: string): RqlQueryResult {
    return {reason, status: 'error'};
}

export function useQuery({rqlClient, readonly}: RqlQuerierProps) {
    const [query, setQuery] = React.useState('');
    const [resultsAndVariables, setResults] = React.useState<{
        results: RqlQueryResult | null;
        variables: string[] | null;
    }>({results: null, variables: null});
    const [queryIsLoading, setQueryIsLoading] = React.useState<boolean>(false);
    const [parser] = React.useState<RqlParser>(new RqlParser());
    const [isForbiddenQuery, setIsForbiddenQuery] = React.useState(false);

    React.useEffect(() => {
        if (readonly && /SET|INSERT|DELETE/.test(query)) {
            setIsForbiddenQuery(true);
        } else {
            setIsForbiddenQuery(false);
        }
    }, [readonly, query]);

    React.useEffect(() => {
        parser.parse(query);
    }, [query, parser]);

    const executeQuery = () => {
        if (isForbiddenQuery) {
            setResults({
                results: rqlQueryResultError(
                    'This is a readonly querier. You cannot perform INSERT, SET or DELETE queries.'
                ),
                variables: null,
            });
            return;
        }
        setQueryIsLoading(true);
        rqlClient
            .queryRows(query)
            .then((rows) => {
                setQueryIsLoading(false);
                setResults({
                    results: rqlQueryResultSucces(rows),
                    variables: getQueryVariables(query),
                });
            })
            .catch((reason) => {
                setQueryIsLoading(false);
                setResults({
                    results: rqlQueryResultError(reason),
                    variables: null,
                });
            });
    };

    return {
        query,
        setQuery,
        resultsAndVariables,
        executeQuery,
        queryIsLoading,
        parser,
        isForbiddenQuery,
    };
}
